import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, AlertTriangle, CheckCircle2, Cpu, DoorOpen, ScanFace, Stethoscope, XCircle } from "lucide-react";

const ST = {
  online: ["Online", "bg-emerald-50 text-emerald-700 border-emerald-300", CheckCircle2],
  offline: ["Offline", "bg-rose-50 text-rose-700 border-rose-300", XCircle],
  erro: ["Erro", "bg-rose-50 text-rose-700 border-rose-300", AlertTriangle],
  sdk_ausente: ["SDK ausente", "bg-amber-50 text-amber-800 border-amber-300", AlertTriangle],
  desativado: ["Desativado", "bg-slate-50 text-slate-600 border-slate-300", XCircle],
  sem_comunicacao: ["Sem comunicação", "bg-amber-50 text-amber-800 border-amber-300", AlertTriangle],
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

const Comp = ({ icon: Icon, title, c, extra, testid }) => {
  const [label, cls, SIcon] = ST[c?.status] || ST.sem_comunicacao;
  return (
    <div className={`rounded-lg border-2 p-3 ${cls}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-bold text-sm"><Icon className="w-4 h-4"/>{title}</div>
        <span className="flex items-center gap-1 text-xs font-bold" data-testid={`${testid}-status`}><SIcon className="w-3.5 h-3.5"/>{label}</span>
      </div>
      <div className="mt-1 text-[11px] text-slate-700 space-y-0.5">
        {extra}
        {c?.detail && <div>{c.detail}</div>}
        <div>Último teste: {fmt(c?.checked_at || c?.last_heartbeat)}</div>
      </div>
    </div>
  );
};

const AgentCard = ({ a, onDiag }) => (
  <Card className="p-4 space-y-3" data-testid={`agent-card-${a.id}`}>
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div><div className="font-heading font-black text-slate-900">{a.name}</div><div className="text-xs text-slate-500">{a.unit_name} · agente v{a.agent.version || "?"}</div></div>
      <Button size="sm" variant="outline" onClick={() => onDiag(a.id)} disabled={!!a.diagnose_requested_at} data-testid={`agent-diagnose-${a.id}`}>
        <Stethoscope className="w-4 h-4 mr-1"/>{a.diagnose_requested_at ? "Diagnóstico solicitado…" : "Diagnosticar (sem liberar)"}
      </Button>
    </div>
    <div className="grid md:grid-cols-3 gap-2">
      <Comp icon={Cpu} title="Agente Windows" c={a.agent} testid={`agent-${a.id}-agent`} extra={<div>Alunos no cache: {a.cache?.students ?? "—"} · eventos pendentes: {a.pending_events}<br/>Última sincronização: {fmt(a.cache?.last_sync)}</div>}/>
      <Comp icon={DoorOpen} title="Catraca TopData" c={a.topdata} testid={`agent-${a.id}-topdata`} extra={<div>IP {a.topdata?.ip || "—"} · Inner {a.topdata?.inner ?? "—"} · porta {a.topdata?.port ?? "—"}</div>}/>
      <Comp icon={ScanFace} title="Leitor Intelbras Bio-T" c={a.biot} testid={`agent-${a.id}-biot`} extra={<div>{a.biot?.url || "—"} {a.biot?.device_type ? `· ${a.biot.device_type}` : ""} {a.biot?.software_version ? `· fw ${a.biot.software_version}` : ""}</div>}/>
    </div>
    <div className="grid md:grid-cols-2 gap-3">
      <div data-testid={`agent-${a.id}-access`}>
        <div className="text-xs font-bold mb-1">Últimos acessos</div>
        <div className="max-h-56 overflow-auto text-[11px] divide-y border rounded">
          {a.recent_access.length === 0 && <div className="p-2 text-slate-500">Nenhum acesso registrado pelo agente.</div>}
          {a.recent_access.map((r) => (
            <div key={r.id} className="p-1.5 flex justify-between gap-2">
              <span>{fmt(r.timestamp)} · {r.student_name} · {r.direction}</span>
              <span className={r.granted ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"}>{r.granted ? "Liberado" : `Negado: ${r.reason || r.reason_code}`}</span>
            </div>
          ))}
        </div>
      </div>
      <div data-testid={`agent-${a.id}-errors`}>
        <div className="text-xs font-bold mb-1">Erros recentes (retenção: {a.error_retention})</div>
        <div className="max-h-56 overflow-auto text-[11px] divide-y border rounded">
          {a.errors.length === 0 && <div className="p-2 text-slate-500">Sem erros.</div>}
          {a.errors.map((e, i) => <div key={i} className="p-1.5"><b>{fmt(e.at)}</b> · {e.msg}</div>)}
        </div>
      </div>
    </div>
  </Card>
);

export const AgentMonitor = () => {
  const [data, setData] = useState(null);
  const load = () => api.get("/devices/agents/monitor").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);
  const diag = async (id) => {
    try { await api.post(`/devices/${id}/agent-diagnose`); toast.success("Diagnóstico solicitado — o agente testa a comunicação no próximo ciclo, sem liberar a catraca."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };
  const alerts = (data?.agents || []).filter((a) => a.agent.status !== "online" || ["offline", "erro", "sem_comunicacao", "sdk_ausente"].includes(a.topdata.status) || ["offline", "erro", "sem_comunicacao"].includes(a.biot.status));
  return (
    <section className="space-y-3" data-testid="agent-monitor">
      <div className="flex items-center gap-2"><Activity className="w-5 h-5 text-rose-600"/><h2 className="font-heading text-lg font-black">Agentes locais — monitoramento</h2><span className="text-xs text-slate-500">atualiza a cada 10 s</span></div>
      {alerts.length > 0 && <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="agent-monitor-alert"><AlertTriangle className="w-4 h-4 inline mr-1"/>{alerts.length} agente(s) com equipamento desconectado, sem comunicação ou com erro.</div>}
      {data && data.agents.length === 0 && <Card className="p-4 text-sm text-slate-500" data-testid="agent-monitor-empty">Nenhum Agente Local v2 conectado ainda.</Card>}
      {(data?.agents || []).map((a) => <AgentCard key={a.id} a={a} onDiag={diag}/>)}
    </section>
  );
};
